import { Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNonBrandPerformanceKpisQuery } from 'src/context/api/non-branded/api';
import { NonBrandKpisResponse } from 'src/context/api/non-branded/models';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { RootState } from 'src/context/store';
import { useLocales } from 'src/locales';
import { fDate } from 'src/utils/format-time';
import FunnelKpiCard from '../funnel/FunnelKpiCard';
import { useNonBrandFilterArgs } from './use-non-brand-filter-args';
import { isLeadWorkspace } from './workspace-type';

type KpiStat = {
  key: string;
  title: string;
  definition: string;
  value: number | null;
  /** `null` = compare payload without a value for this metric; `undefined` = no compare payload. */
  compare?: number | null;
  isPercent?: boolean;
  isCurrency?: boolean;
  invertedColor?: boolean;
};

const BASE = [
  { key: 'non_brand_searches' },
  { key: 'non_brand_traffic' },
  { key: 'blended_ctr', isPercent: true },
  { key: 'ad_cost', isCurrency: true, invertedColor: true },
] as const;

/** Card definitions: the 5th card is Paid ROAS (e-commerce) or Paid CPA (lead). */
export const kpiStats = (
  data: NonBrandKpisResponse | undefined,
  isLead: boolean,
  t: (key: string) => string
): KpiStat[] => {
  const last = isLead ? { key: 'paid_cpa', isCurrency: true, invertedColor: true } : { key: 'paid_roas' };
  return [...BASE, last].map((stat: any) => ({
    ...stat,
    title: t(`dashboard.nonBrand.kpis.${stat.key}.title`),
    definition: t(`dashboard.nonBrand.kpis.${stat.key}.definition`),
    value: (data?.main as any)?.[stat.key] ?? null,
    compare: data?.compare ? (data.compare as any)[stat.key] ?? null : undefined,
  }));
};

export default function NbKpiCards() {
  const { t } = useLocales();
  const workspace = useCurrentWorkspace();
  const filters = useSelector((state: RootState) => state.filters);
  const args = useNonBrandFilterArgs();
  const isLead = isLeadWorkspace(workspace?.template);
  const { data, isFetching } = useNonBrandPerformanceKpisQuery(
    {
      ...args,
      compare: filters.compare,
      start_compare: fDate(filters.compareStart, 'yyyy-MM-dd'),
      end_compare: fDate(filters.compareEnd, 'yyyy-MM-dd'),
      opportunity: filters.nonBrandOpportunity,
    },
    { skip: !workspace?.id || !filters.start || !filters.end }
  );

  return (
    <Grid container spacing={1.5}>
      {kpiStats(data, isLead, t).map((stat) => (
        <Grid
          item
          key={stat.key}
          xs={6}
          md={4}
          sx={{ '@media (min-width:1200px)': { flexBasis: '20%', maxWidth: '20%' } }}
        >
          <FunnelKpiCard
            isFetching={isFetching}
            title={stat.title}
            definition={stat.definition}
            total={stat.value}
            compare={stat.compare}
            isPercent={stat.isPercent}
            isCurrency={stat.isCurrency}
            invertedColor={stat.invertedColor}
            hideInfinity
            valueFontSize={20}
          />
        </Grid>
      ))}
    </Grid>
  );
}
